/**
 * Contratto condiviso per la navigazione documentale.
 *
 * In questa fase le funzioni sono intenzionalmente indipendenti da React Router:
 * possono essere usate dalla pagina Documenti, dal tab File del contatto e dai
 * link prodotti dalla chat senza duplicare la conoscenza della struttura URL.
 */

export const DOCUMENTS_ROOT_PATH = '/documents';

export const DOCUMENT_OPERATIONAL_VIEWS = [
  'recent',
  'mine',
  'processing',
  'issues',
  'trash',
] as const;

export type DocumentOperationalView = typeof DOCUMENT_OPERATIONAL_VIEWS[number];

export type DocumentNavigationTarget =
  | { kind: 'home' }
  | { kind: 'space'; spaceId: number }
  | { kind: 'folder'; spaceId: number; folderId: number }
  | { kind: 'file'; spaceId: number; fileId: number }
  | { kind: 'view'; view: DocumentOperationalView };

const POSITIVE_INTEGER = /^[1-9]\d*$/;

const isPositiveInteger = (value: number): boolean =>
  Number.isSafeInteger(value) && value > 0;

const requirePositiveInteger = (name: string, value: number): void => {
  if (!isPositiveInteger(value)) {
    throw new TypeError(`${name} must be a positive safe integer`);
  }
};

const parsePositiveInteger = (value: string): number | null => {
  if (!POSITIVE_INTEGER.test(value)) return null;
  const parsed = Number(value);
  return isPositiveInteger(parsed) ? parsed : null;
};

const isOperationalView = (value: string): value is DocumentOperationalView =>
  DOCUMENT_OPERATIONAL_VIEWS.some((view) => view === value);

/** Genera il path canonico associato a una destinazione documentale. */
export const buildDocumentPath = (target: DocumentNavigationTarget): string => {
  switch (target.kind) {
    case 'home':
      return DOCUMENTS_ROOT_PATH;
    case 'space':
      requirePositiveInteger('spaceId', target.spaceId);
      return `${DOCUMENTS_ROOT_PATH}/spaces/${target.spaceId}`;
    case 'folder':
      requirePositiveInteger('spaceId', target.spaceId);
      requirePositiveInteger('folderId', target.folderId);
      return `${DOCUMENTS_ROOT_PATH}/spaces/${target.spaceId}/folders/${target.folderId}`;
    case 'file':
      requirePositiveInteger('spaceId', target.spaceId);
      requirePositiveInteger('fileId', target.fileId);
      return `${DOCUMENTS_ROOT_PATH}/spaces/${target.spaceId}/files/${target.fileId}`;
    case 'view':
      return `${DOCUMENTS_ROOT_PATH}/views/${target.view}`;
  }
};

/**
 * Interpreta esclusivamente URL documentali completi e validi.
 * Query string, hash, segmenti aggiuntivi e identificativi non positivi vengono
 * rifiutati, lasciando al router la gestione della pagina non trovata.
 */
export const parseDocumentPath = (pathname: string): DocumentNavigationTarget | null => {
  if (typeof pathname !== 'string') return null;

  const normalized = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

  if (normalized === DOCUMENTS_ROOT_PATH) return { kind: 'home' };

  const segments = normalized.split('/').filter(Boolean);
  if (segments[0] !== 'documents') return null;

  if (segments.length === 3 && segments[1] === 'views' && isOperationalView(segments[2])) {
    return { kind: 'view', view: segments[2] };
  }

  if (segments[1] !== 'spaces') return null;
  const spaceId = parsePositiveInteger(segments[2] ?? '');
  if (spaceId === null) return null;

  if (segments.length === 3) return { kind: 'space', spaceId };
  if (segments.length !== 5) return null;

  const nodeId = parsePositiveInteger(segments[4]);
  if (nodeId === null) return null;

  if (segments[3] === 'folders') {
    return { kind: 'folder', spaceId, folderId: nodeId };
  }
  if (segments[3] === 'files') {
    return { kind: 'file', spaceId, fileId: nodeId };
  }
  return null;
};

export const documentTargetsEqual = (
  left: DocumentNavigationTarget | null,
  right: DocumentNavigationTarget | null,
): boolean => {
  if (left === null || right === null) return left === right;
  return buildDocumentPath(left) === buildDocumentPath(right);
};
